import {
  DepressionContentPage,
  FAQItem,
} from "@/components/DepressionContentPage";
import { Link } from "@/lib/router-compat";
import { RelatedLink } from "@/components/RelatedPages";
import { SaintComparisonStatBand } from "@/components/SaintComparisonInline";

const faqs: FAQItem[] = [
  {
    question: "Can children really have depression?",
    answer:
      "Yes. Childhood depression is real, more common than most people assume, and treatable. Roughly 3 percent of children and 8 percent of adolescents in the U.S. are affected. It often goes unrecognized because it doesn't always look like adult depression — younger children may show it through anger and acting out rather than visible sadness.",
  },
  {
    question:
      "What's the difference between a bad week and depression in a child?",
    answer:
      "The distinction that matters is persistence and interference. A child who's sad for a few days is having a hard week. A child whose mood has been low, angry, or withdrawn for two weeks or more, and whose schoolwork, friendships, or family life are suffering because of it, may have a depressive illness.",
  },
  {
    question: "Does my own depression affect my child's risk?",
    answer:
      "Children with a parent who has depression are at significantly higher risk of developing it themselves, and tend to have their first episode earlier. That's not a reason for guilt — depression has a substantial genetic component. But it's a reason for attention. Studies also show that when a depressed parent gets effective treatment and reaches remission, their children's symptoms often improve, even without direct treatment of the child.",
  },
  {
    question: "Is SAINT cleared for children or teenagers?",
    answer:
      "No. SAINT is FDA-cleared for adults with major depressive disorder who haven't gotten adequate relief from antidepressants in their current episode. It is not cleared for children or teenagers. However, if you're a parent living with your own treatment-resistant depression, getting effective treatment can be one of the most important things you do for your family.",
  },
  {
    question: "What should I do if I think my child is depressed?",
    answer:
      "If the signs have lasted two weeks or more, schedule a visit with your child's pediatrician to rule out physical causes and get a referral to a mental health professional who works with children. Treatment usually starts with psychotherapy. Medication may be considered if therapy alone isn't enough, with careful monitoring. If your child talks about suicide, gives away possessions, or withdraws sharply, treat it as urgent — call or text 988 or go to your nearest emergency room.",
  },
  {
    question: "Do I have to stop my medications to do SAINT as a parent?",
    answer:
      "No. Most patients continue their existing medication regimen through SAINT treatment. SAINT is not a replacement for what you've built — it is a different kind of intervention aimed at the brain circuitry your medications may not have reached. Never change medication without guidance from your prescribing provider.",
  },
];

const related: RelatedLink[] = [
  {
    title:
      '"It Runs in My Family." What That Actually Means for Your Depression',
    href: "/it-runs-in-my-family-what-that-means-for-your-depression",
    description:
      "How family history shapes depression risk — and why it doesn't decide whether treatment works.",
  },
  {
    title:
      "High-Functioning Depression: When Holding It Together Is the Problem",
    href: "/high-functioning-depression-when-holding-it-together-is-the-problem",
    description:
      "Functioning isn't the same as being okay — and why severe depression hides behind a polished exterior.",
  },
  {
    title: "You've Tried Everything. Why Is the Depression Still Here?",
    href: "/youve-tried-everything-why-is-the-depression-still-here",
    description:
      "What treatment-resistant depression means and why medications don't work for everyone.",
  },
  {
    title: "What Is SAINT TMS Therapy?",
    href: "/learn/what-is-saint-tms",
    description: "How the five-day, fMRI-guided protocol works.",
  },
];

export default function IsMyChildDepressed() {
  return (
    <DepressionContentPage
      seo={{
        title:
          "Is My Child Depressed? What Parents Should Know | Neuro Wellness",
        description:
          "Signs of childhood depression, when to seek help, why your own depression matters, and how a parent's recovery changes a child's prognosis.",
        canonical: "/is-my-child-depressed-what-parents-should-know",
        keywords:
          "childhood depression, depressed child, parent depression, children and depression, SAINT TMS for parents, treatment-resistant depression, family history depression, child mental health, pediatric depression signs",
      }}
      badge="For Families"
      heroTitle="Is My Child Depressed? What Parents Should Know, and Why Your Own Depression Is Part of the Answer"
      heroSubtitle="Every kid has bad days. But sometimes the bad days don't end. If you're a parent wondering whether something is actually wrong — and what your own mental health has to do with it — this is for you."
      lastUpdated="2026-11-03"
      sections={[
        {
          body: (
            <div className="space-y-5">
              <p>
                Every kid has bad days. They come home from school sullen, slam
                a door, cry over something that seems small, and by the weekend
                they've forgotten it. That's childhood. It's not depression.
              </p>
              <p>
                But sometimes the bad days don't end. The irritability becomes
                the default. The kid who used to run out the door to see friends
                starts staying in their room. Grades slip. Stomachaches and
                headaches show up that the pediatrician can't explain. And a
                parent starts wondering, quietly and with a lot of guilt,
                whether something is actually wrong.
              </p>
              <p>
                If you're that parent, this article is for you. It covers what
                childhood depression looks like, when to seek help, and one
                thing most articles on this subject leave out: what your own
                mental health has to do with it.
              </p>
            </div>
          ),
        },
        {
          heading: "Yes, children can have depression",
          body: (
            <div className="space-y-5">
              <p>
                Childhood depression is real, it's more common than most people
                assume, and it's treatable. Roughly 3 percent of children and 8
                percent of adolescents in the U.S. are affected. Before age 10
                it's more common in boys; by the mid-teens, girls are affected
                more often.
              </p>
              <p>
                The reason it goes unrecognized is that it doesn't always look
                like adult depression. Younger children especially may show it
                through anger and acting out rather than visible sadness.
                Clinicians used to call this "masked" depression. Many kids do
                show the low mood directly, but a parent watching for tears can
                miss a child whose depression is showing up as a short fuse.
              </p>
            </div>
          ),
        },
        {
          heading: "What to look for",
          body: (
            <div className="space-y-5">
              <p>
                The distinction that matters is persistence and interference. A
                child who's sad for a few days is having a hard week. A child
                whose mood has been low, angry, or withdrawn for two weeks or
                more, and whose schoolwork, friendships, or family life are
                suffering because of it, may have a depressive illness.
              </p>
              <p>Signs parents commonly notice:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Irritability or anger that seems out of proportion</li>
                <li>Persistent sadness or a sense of hopelessness</li>
                <li>Pulling away from friends, activities, and family</li>
                <li>Heightened sensitivity to rejection or criticism</li>
                <li>Changes in appetite or sleep, in either direction</li>
                <li>Crying spells or emotional outbursts</li>
                <li>Trouble concentrating, slipping grades</li>
                <li>Low energy and fatigue</li>
                <li>
                  Physical complaints like stomachaches or headaches that don't
                  respond to treatment
                </li>
                <li>Expressions of worthlessness or guilt</li>
                <li>Any talk of death or not wanting to be here</li>
              </ul>
              <p>
                No child shows all of these, and the picture often shifts across
                settings. Some kids hold it together at school and fall apart at
                home, or the reverse. What parents should watch for is a
                noticeable change from how the child used to be.
              </p>
            </div>
          ),
        },
        {
          heading: "The family history piece",
          body: (
            <div className="space-y-5">
              <p>
                Here's something the research is clear on: children with a
                parent who has depression are at significantly higher risk of
                developing it themselves, and they tend to have their first
                episode earlier than children whose parents don't.
              </p>
              <p>
                That's not a reason for guilt. Depression has a substantial
                genetic component; you didn't choose it any more than you chose
                your eye color. But it's a reason for attention. If you're a
                parent living with depression or anxiety, your child's risk is
                elevated, and the signs above deserve a lower threshold for
                concern.
              </p>
              <p>
                It cuts the other way too. Studies following families over time
                have found that when a depressed parent gets effective treatment
                and reaches remission, their children's own symptoms improve,
                even when the children aren't treated directly. A parent's
                recovery changes the emotional weather of the household. Kids
                feel it.
              </p>
            </div>
          ),
        },
        {
          heading: "When to seek help for your child",
          body: (
            <div className="space-y-5">
              <p>
                If the signs have lasted two weeks or more, schedule a visit
                with your child's pediatrician to rule out physical causes and
                get a referral to a mental health professional who works with
                children. A proper evaluation includes conversations with you
                and with your child, often separately, and may draw on input
                from teachers.
              </p>
              <p>
                Treatment for children usually starts with psychotherapy.
                Medication is considered if therapy alone isn't enough, with
                careful monitoring, because antidepressants carry an FDA warning
                about suicidal thinking in young people. The combination of
                therapy and medication has the strongest evidence.
              </p>
              <p>
                For teenagers whose depression hasn't responded to those steps,
                some standard TMS systems have received FDA clearance for
                adolescents aged 15 and older as an add-on treatment. That's
                worth discussing with your child's psychiatrist.
              </p>
            </div>
          ),
        },
        {
          heading: "What SAINT means for you, the parent",
          body: (
            <div className="space-y-4">
              <p>
                SAINT (Stanford Accelerated Intelligent Neuromodulation Therapy)
                is an accelerated, FDA-cleared form of transcranial magnetic
                stimulation, developed at Stanford's Brain Stimulation Lab. It's
                cleared for adults with major depressive disorder who haven't
                gotten adequate relief from antidepressants in their current
                episode. It is not cleared for children or teenagers, and we
                won't pretend otherwise.
              </p>
              <p>
                But if you're a parent who has been living with your own
                depression for years, cycling through medications that only
                partly work, while now also worrying about your child, SAINT may
                be the most important thing you do for your family.
              </p>
              <p>
                Depression isn't only a chemistry problem; it's also a circuitry
                problem. The region behind the forehead that regulates mood is
                underactive in depression, and medication doesn't directly
                retrain it. SAINT uses a functional MRI of your own brain to
                locate the precise target, then delivers focused magnetic
                stimulation to that spot. No anesthesia, nothing entering your
                bloodstream.
              </p>

              <SaintComparisonStatBand />

              <p>
                Here's how it works at a{" "}
                <Link
                  to="/find-a-verified-saint-provider"
                  className="font-semibold text-primary hover:underline"
                >
                  Verified SAINT Provider
                </Link>{" "}
                like Neuro Wellness TMS Centers of America:
              </p>
              <ol className="list-decimal pl-5 space-y-3">
                <li>
                  <strong>Consultation.</strong> You meet with our clinical team
                  to review your history and medication trials and confirm
                  you're a candidate. The initial consultation is a Zoom call;
                  as steps evolve, you'll also have the option to meet with our
                  psychiatrist by Zoom web conference or in person. The
                  mandatory in-person steps include fMRI mapping and the actual
                  treatment.
                </li>
                <li>
                  <strong>fMRI brain scan.</strong> A roughly 45-minute
                  functional MRI maps how your individual brain networks are
                  wired.
                </li>
                <li>
                  <strong>Personalized targeting.</strong> A targeting algorithm
                  reads your scan and identifies the exact location for
                  stimulation. Neuronavigation guides the coil there for every
                  session.
                </li>
                <li>
                  <strong>Five-day stimulation series.</strong> Ten 10-minute
                  sessions per day, with 50-minute breaks in between, for five
                  consecutive days. Five clinic visits instead of the 30 to 36
                  required for conventional TMS. One arranged week of childcare,
                  not two months of daily appointments.
                </li>
              </ol>
              <p>
                In the clinical trials behind its clearance, 79 percent of
                participants reached remission, with an average time to
                remission of 2.6 days. Across more than 400 trial patients, 85
                percent experienced symptom relief, with no serious adverse
                events. Side effects were generally mild: headache, fatigue, and
                temporary scalp discomfort. SAINT isn't appropriate for people
                with non-removable metal in or near the head or an implanted
                device. You don't have to stop your current medications.
              </p>
            </div>
          ),
        },
        {
          heading: "Two things at once",
          body: (
            <div className="space-y-4">
              <p>
                Getting your child evaluated and getting yourself treated aren't
                competing priorities. They're the same project. A child with a
                depressed parent who is actively recovering has a better
                prognosis than a child with a depressed parent who is
                white-knuckling it. Modeling that help is available, and that it
                works, is one of the most protective things you can show a kid.
              </p>
            </div>
          ),
        },
        {
          heading: "What to do next",
          body: (
            <div className="space-y-4">
              <p>
                If you recognized your child in this article, call their
                pediatrician this week. If you recognized yourself, call us.
              </p>
              <p>
                At Neuro Wellness TMS Centers of America, the first appointment
                is an evaluation, not a commitment. We'll review your history
                and tell you honestly whether SAINT is a good fit. We offer
                private outpatient care with a dedicated care team and weekend
                availability, because we know parents don't have weekdays to
                spare. Insurance coverage varies by plan; our team can help you
                understand your options, including prior authorization and
                self-pay.
              </p>
              <p className="font-semibold text-primary">
                Schedule a consultation:{" "}
                <a
                  href="tel:9548279793"
                  className="text-primary hover:underline"
                >
                  954-827-9793
                </a>
              </p>
              <p className="text-xs text-muted-foreground italic pt-2">
                If your child talks about suicide, gives away possessions,
                withdraws sharply, or shows a sudden focus on death, treat it as
                urgent. Call or text 988 (Suicide and Crisis Lifeline) or go to
                your nearest emergency room. This article is for educational
                purposes and is not a substitute for individual medical advice.
              </p>
            </div>
          ),
        },
      ]}
      faq={faqs}
      relatedLinks={related}
      finalCtaText="Schedule a Consultation"
    />
  );
}
